import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FileSpreadsheet, PlusCircle, FolderPlus, UserPen } from "lucide-react";
import { api, type ClusterView, type KeywordSummary, type Status } from "../../lib/api.js";
import { ClusterStrip } from "./ClusterStrip.js";
import { KeywordTable } from "./KeywordTable.js";
import { KeywordDetailPanel } from "./KeywordDetailPanel.js";
import { AddKeywordDialog } from "./AddKeywordDialog.js";
import { NewClusterDialog } from "./NewClusterDialog.js";
import { ImportWizard } from "./ImportWizard.js";
import { ExpandClusterDialog } from "./ExpandClusterDialog.js";
import { SplitClusterDialog } from "./SplitClusterDialog.js";
import { GuestPostDialog } from "./GuestPostDialog.js";

/**
 * Top-level Pipeline view. Composes:
 *   - ClusterStrip   : horizontally-scrollable cluster cards (acts as a filter)
 *   - KeywordTable   : filterable keyword rows
 *   - KeywordDetailPanel : slide-out from the right when a row is selected
 *
 * State held here:
 *   - selectedClusterId : null = "all"; clicking a card filters the table
 *   - statusFilter      : null = "all"; chip group
 *   - searchQuery       : free-text filter
 *   - selectedKeywordId : null = no panel open
 */
export function PipelinePage() {
  const [selectedClusterId, setSelectedClusterId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<Status | null>(null);
  // Covered = a LIVE page already serves this keyword (migration 043). Hidden by
  // default: they are not production work, and leaving them in makes the queue
  // look ~1.8k items deeper than it is.
  const [hideCovered, setHideCovered] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedKeywordId, setSelectedKeywordId] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [newClusterOpen, setNewClusterOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [guestOpen, setGuestOpen] = useState(false);
  const [expandCluster, setExpandCluster] = useState<ClusterView | null>(null);
  const [splitCluster, setSplitCluster] = useState<ClusterView | null>(null);
  const qc = useQueryClient();

  // Deep-link: `#/pipeline?open=<keywordId>` opens that keyword's detail panel.
  //
  // `id` is accepted as well, and that is not politeness — it was the bug. Five
  // call sites (Tasks → Open, Articles, Distribution, and the primary/secondary
  // links inside this very panel) wrote `?id=`, while this reader only ever
  // looked for `open`. Every one of them navigated to Pipeline and then left the
  // operator to find the keyword by hand, which is exactly what it looked like:
  // nothing happened. Only Content DB used the spelling the reader wanted.
  //
  // Producers are now on `open`; `id` stays readable so older links and anything
  // bookmarked keeps working.
  useEffect(() => {
    const readOpen = () => {
      const q = window.location.hash.split("?")[1];
      const params = q ? new URLSearchParams(q) : null;
      const id = params?.get("open") ?? params?.get("id") ?? null;
      if (id) setSelectedKeywordId(id);
    };
    readOpen();
    window.addEventListener("hashchange", readOpen);
    return () => window.removeEventListener("hashchange", readOpen);
  }, []);

  const clustersQuery = useQuery({ queryKey: ["pipeline", "clusters"], queryFn: () => api.listClusters() });

  const keywordsQuery = useQuery({
    queryKey: ["pipeline", "keywords", { clusterId: selectedClusterId, status: statusFilter, q: searchQuery }],
    queryFn: () => api.listKeywords({
      clusterId: selectedClusterId ?? undefined,
      status: statusFilter ? [statusFilter] : undefined,
      q: searchQuery || undefined,
    }),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold">Pipeline</h1>
          <p className="mt-1 text-sm text-muted">
            Every keyword in flight + every cluster. Click a cluster to filter, click a row to open the detail panel.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <button onClick={() => setNewClusterOpen(true)} className="btn-secondary btn-sm">
            <FolderPlus className="h-3.5 w-3.5" />
            New cluster
          </button>
          <button onClick={() => setImportOpen(true)} className="btn-secondary btn-sm">
            <FileSpreadsheet className="h-3.5 w-3.5" />
            Import CSV
          </button>
          <button onClick={() => setGuestOpen(true)} className="btn-secondary btn-sm" title="Bring your own article body + author bio; the system generates meta/images/links and publishes.">
            <UserPen className="h-3.5 w-3.5" />
            Guest post
          </button>
          <button onClick={() => setAddOpen(true)} className="btn-primary btn-sm">
            <PlusCircle className="h-3.5 w-3.5" />
            Add keyword
          </button>
        </div>
      </div>

      <div className="flex items-center gap-6 text-right">
        <div>
          <div className="text-2xl font-semibold text-brand">{clustersQuery.data?.clusters.length ?? "—"}</div>
          <div className="text-xs uppercase tracking-wide text-muted">clusters</div>
        </div>
        <div>
          <div className="text-2xl font-semibold text-brand">{keywordsQuery.data?.count ?? "—"}</div>
          <div className="text-xs uppercase tracking-wide text-muted">keywords</div>
        </div>
      </div>

      <ClusterStrip
        clusters={clustersQuery.data?.clusters ?? []}
        loading={clustersQuery.isPending}
        selectedId={selectedClusterId}
        onSelect={setSelectedClusterId}
        onExpand={(c) => setExpandCluster(c)}
        onSplit={(c) => setSplitCluster(c)}
      />

      <FilterBar
        status={statusFilter}
        onStatusChange={setStatusFilter}
        search={searchQuery}
        onSearchChange={setSearchQuery}
        hideCovered={hideCovered}
        onHideCoveredChange={setHideCovered}
        coveredCount={(keywordsQuery.data?.keywords ?? []).filter((k) => k.coveredByUrl).length}
      />

      {keywordsQuery.data && keywordsQuery.data.shown != null && keywordsQuery.data.shown < keywordsQuery.data.count && (
        <div className="rounded-md bg-amber-50/60 px-3 py-1.5 text-2xs text-amber-700">
          Showing {keywordsQuery.data.shown.toLocaleString()} of {keywordsQuery.data.count.toLocaleString()} — narrow with the status / cluster / search filters above.
        </div>
      )}

      <KeywordTable
        keywords={(keywordsQuery.data?.keywords ?? []).filter((k) => !hideCovered || !k.coveredByUrl)}
        loading={keywordsQuery.isPending}
        onSelect={(k: KeywordSummary) => setSelectedKeywordId(k.id)}
        selectedId={selectedKeywordId}
      />

      {selectedKeywordId && (
        <KeywordDetailPanel
          keywordId={selectedKeywordId}
          onClose={() => setSelectedKeywordId(null)}
        />
      )}

      {addOpen && (
        <AddKeywordDialog
          defaultClusterId={selectedClusterId}
          onCancel={() => setAddOpen(false)}
          onCreated={(newId) => {
            setAddOpen(false);
            qc.invalidateQueries({ queryKey: ["pipeline"] });
            setSelectedKeywordId(newId);
          }}
        />
      )}

      {newClusterOpen && (
        <NewClusterDialog
          onCancel={() => setNewClusterOpen(false)}
          onCreated={(clusterId) => {
            setNewClusterOpen(false);
            qc.invalidateQueries({ queryKey: ["pipeline", "clusters"] });
            setSelectedClusterId(clusterId);
          }}
        />
      )}

      {importOpen && (
        <ImportWizard
          onClose={() => {
            setImportOpen(false);
            qc.invalidateQueries({ queryKey: ["pipeline"] });
          }}
        />
      )}

      {expandCluster && (
        <ExpandClusterDialog
          cluster={expandCluster}
          onClose={() => {
            setExpandCluster(null);
            qc.invalidateQueries({ queryKey: ["pipeline"] });
          }}
        />
      )}

      {splitCluster && (
        <SplitClusterDialog
          cluster={splitCluster}
          onClose={() => {
            setSplitCluster(null);
            qc.invalidateQueries({ queryKey: ["pipeline"] });
          }}
        />
      )}

      {guestOpen && (
        <GuestPostDialog
          onCancel={() => setGuestOpen(false)}
          onCreated={(keywordId) => {
            setGuestOpen(false);
            qc.invalidateQueries({ queryKey: ["pipeline"] });
            setSelectedKeywordId(keywordId);
          }}
        />
      )}
    </div>
  );
}

const STATUSES: Status[] = [
  "backlog",
  "researching",
  "briefed",
  "drafting",
  "reviewing",
  "published",
  "distributed",
];

function FilterBar({
  status,
  onStatusChange,
  search,
  onSearchChange,
  hideCovered,
  onHideCoveredChange,
  coveredCount,
}: {
  hideCovered: boolean;
  onHideCoveredChange: (v: boolean) => void;
  coveredCount: number;
  status: Status | null;
  onStatusChange: (s: Status | null) => void;
  search: string;
  onSearchChange: (q: string) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <input
        type="text"
        placeholder="Search keywords, cluster, id…"
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        className="input max-w-xs"
      />
      <div className="flex flex-wrap gap-1">
        <FilterChip active={status === null} onClick={() => onStatusChange(null)}>All</FilterChip>
        <label
          className="ml-2 flex cursor-pointer items-center gap-1.5 text-2xs text-muted"
          title="Covered = a live page already serves this keyword. Producing an article for it would compete with our own page."
        >
          <input type="checkbox" checked={hideCovered} onChange={(e) => onHideCoveredChange(e.target.checked)} />
          Hide covered{coveredCount > 0 ? ` (${coveredCount})` : ""}
        </label>
        {STATUSES.map((s) => (
          <FilterChip key={s} active={status === s} onClick={() => onStatusChange(s)}>
            {s}
          </FilterChip>
        ))}
      </div>
    </div>
  );
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={
        active
          ? "rounded-full bg-brand px-3 py-1 text-xs font-medium text-white"
          : "rounded-full bg-line px-3 py-1 text-xs font-medium text-muted hover:bg-line/80 hover:text-ink"
      }
    >
      {children}
    </button>
  );
}
